import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewService } from '@services/reviewService';
import { WorkpayApiError } from '@workpay/ds-shared/services/apiEnvelope';
import { Button } from '@workpay/ds-shared/components/ui/Button';
import { ReviewerActions } from '@workpay/ds-shared/components/ReviewerActions';
import { Modal } from '@workpay/ds-shared/components/ui/Modal';
import { ReviewActivityPanel } from '@workpay/ds-shared/components/ReviewActivityPanel';
import { ReviewerChrome } from '@workpay/ds-shared/components/ReviewerChrome';
import { PdfReviewViewer } from '@workpay/ds-shared/components/PdfReviewViewer';
import { useReviewSse } from '@workpay/ds-shared/hooks/useReviewSse';
import { useDsConfig } from '@workpay/ds-shared/context/DsProvider';
import { API_BASE } from '@lib/runtimeConfig';
import { useTranslation } from '@hooks/useTranslation';
import { LocaleSwitcher } from '@components/LocaleSwitcher/LocaleSwitcher';
import type { DocumentReview, ReviewActivity } from '@workpay/ds-shared/types/review';
import styles from './ReviewPage.module.scss';

// External reviewer file URL — the reviewer has no session, so a PDF is read through the
// token: `GET /review-session/{token}/documents/{fileId}` serves only a file of that review.
// (The `/v1/onlyoffice/file/{fileId}` this used before never existed, so every PDF in an
// emailed review failed with a 401.)
function reviewerFileUrl(token: string, fileId: string): string {
  // Must be absolute on deployed envs — a relative URL resolves against the
  // Pages origin (external.<apex>), which serves the SPA's index.html for any
  // /api/* path. API_BASE is '' on localhost (Vite proxy handles it).
  const path = `/review-session/${encodeURIComponent(token)}/documents/${fileId}`;
  return `${API_BASE}/api/contract-service/v1${path}`;
}

interface ReviewPageProps {
  token: string;
}

export function ReviewPage({ token }: ReviewPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [completed, setCompleted] = useState(false);

  const { data: session, isLoading, error } = useQuery({
    queryKey: ['review-session', token],
    queryFn: () => reviewService.loadSession(token),
    retry: false,
  });

  const documents = useMemo(() => session?.documents ?? [], [session?.documents]);
  const isMultiDoc = documents.length > 1;
  const [activeDocId, setActiveDocId] = useState<string | null>(null);
  const [documentReadyFor, setDocumentReadyFor] = useState<string | null>(null);
  // Derive resolved doc id — falls back to first document if none selected
  const resolvedDocId = activeDocId ?? documents[0]?.id ?? null;
  const activeDocument = documents.find(d => d.id === resolvedDocId) ?? null;
  // Stable, so the surface below does not remount the editor on every parent render.
  const handleDocumentReady = useCallback(
    () => setDocumentReadyFor(resolvedDocId ?? 'single'),
    [resolvedDocId],
  );

  /**
   * ds-external's query keys are `['review-history'|'review-activities', token]` — no `ds-`
   * prefix — so the panel's own default invalidation matches nothing in this app's cache and
   * a posted note would sit invisible until the 10s refetch, which is how people double-post.
   */
  const handleNoteSent = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['review-history', token] });
    void queryClient.invalidateQueries({ queryKey: ['review-activities', token] });
  }, [queryClient, token]);

  const { data: reviewHistory = [], isLoading: historyLoading } = useQuery({
    queryKey: ['review-history', token],
    queryFn: () => reviewService.getReviewHistory(token),
    enabled: !!session,
    refetchInterval: 10_000,
  });

  const { data: sessionActivities = [], isLoading: activitiesLoading } = useQuery({
    queryKey: ['review-activities', token],
    queryFn: () => reviewService.getSessionActivities(token),
    enabled: !!session && reviewHistory.length === 0,
    refetchInterval: 10_000,
  });

  const reviews: DocumentReview[] = useMemo(() => {
    if (reviewHistory.length > 0) return reviewHistory;
    if (!session) return [];
    return [{
      id: session.reviewId,
      targetType: session.targetType,
      targetId: session.targetId ?? '',
      fileId: session.fileId,
      status: session.reviewStatus as DocumentReview['status'],
      initiatedById: '',
      initiatedByName: '',
      initiatedByEmail: '',
      message: session.message,
      authorCompleted: false,
      reviewerCompleted: false,
      reviewers: [],
      activities: sessionActivities as ReviewActivity[],
      createdOn: new Date().toISOString(),
      updatedOn: new Date().toISOString(),
    }];
  }, [reviewHistory, session, sessionActivities]);

  useReviewSse({
    mode: 'reviewer',
    token,
    targetType: session?.targetType,
    targetId: session?.targetId,
    enabled: !!session,
    // ds-shared defaults baseUrl to the relative '/api/contract-service', which
    // resolves against the Pages origin on deployed envs (EventSource then gets
    // index.html / text/html and silently dies). Same fix as tenant-onboarding's
    // 2026-06-19 SSE baseUrl change; API_BASE is '' locally so dev is unchanged.
    baseUrl: `${API_BASE}/api/contract-service`,
  });

  const isReviewCompleted = completed || session?.reviewStatus === 'COMPLETED' || session?.reviewStatus === 'CANCELLED';

  /**
   * WP-14971 — false once THIS reviewer has finished, even though the review itself is still
   * open for the others. The old screen had no such notion: a finished reviewer could reopen
   * their emailed link and keep editing a document nobody would read again.
   *
   * Absent on an older backend, where the safe reading is "still editable" — the review-level
   * status check above already covers the closed case.
   */
  const canEdit = session?.canEdit !== false;

  /** Which reviewer action is being confirmed. */
  const [pendingAction, setPendingAction] = useState<'submit' | 'complete' | null>(null);
  // ds-external deliberately wires no app-level error toast (see lib/axios.ts), so a failed
  // action used to clear the spinner and say nothing — inside a modal the reviewer cannot act
  // on. Render the refusal in the dialog itself; `description` is the backend's own message.
  const [actionError, setActionError] = useState<string | null>(null);
  /**
   * `description` on a refusal is a translation KEY (`K_REVIEWER_ALREADY_FINISHED`), not a
   * sentence. It was rendered raw, so the dialog showed the token to an external reviewer who
   * has no idea what it means. Same shape as the portal's `t(err.description, err.description)`.
   */
  const actionErrorFrom = (err: unknown): string => {
    const e = err as { description?: string; message?: string };
    const generic = t('K_DS_EXT_REVIEW_ACTION_FAILED', 'That did not go through. Try again.');
    if (e?.description) {
      // Empty fallback on purpose: `t` returns it when the bundle has no value for the key,
      // which is how an unseeded refusal is caught before it reaches an external reviewer as
      // a raw `K_` token. `t(description, description)` would print the token instead.
      const translated = t(e.description, '');
      if (translated) return translated;
    }
    return e?.message || generic;
  };

  const submitMutation = useMutation({
    mutationFn: () => reviewService.submitChanges(token),
    onSuccess: () => {
      setPendingAction(null);
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['review-history', token] });
      queryClient.invalidateQueries({ queryKey: ['review-activities', token] });
      /* On a SIGNING REQUEST, submitting changes ends this reviewer's turn:
         `doSubmitReviewerChanges` sets the reviewer REVIEWED and the session's
         `canEdit` is `!finishedOnEnvelope(...)`. Without this re-read the screen kept the
         stale `canEdit: true`, left both buttons up, and the next click — Mark as reviewed —
         came back refused with `K_REVIEWER_ALREADY_FINISHED`. A template review is
         unaffected: `canEdit` stays true there, which is why this is a refetch rather than a
         local flag. */
      queryClient.invalidateQueries({ queryKey: ['review-session', token] });
    },
    onError: (err) => setActionError(actionErrorFrom(err)),
  });

  const completeMutation = useMutation({
    mutationFn: () => reviewService.markReviewerComplete(token),
    onSuccess: () => {
      setPendingAction(null);
      setActionError(null);
      setCompleted(true);
    },
    onError: (err) => setActionError(actionErrorFrom(err)),
  });

  // Both buttons disable while either mutation is in flight — prevents the
  // unclicked button from staying interactive during the API call and keeps
  // the loader visible on the clicked one until the response arrives.
  const anyActionPending = submitMutation.isPending || completeMutation.isPending;
  /**
   * Which document has finished loading (WP-14961).
   *
   * Stored as the document's id rather than a boolean so switching documents in a multi-doc
   * review re-arms the gate: the surface remounts, and the previous document having rendered
   * says nothing about the next one. `'single'` stands in for a review with no document list.
   */
  // Request changes and Mark as reviewed are decisions ABOUT the document, so neither is
  // offered until it is on screen. A failed load releases the gate too — see the surface.
  // `ReviewerActions` folds this together with the in-flight check itself, which is why this
  // screen no longer derives a combined flag of its own.
  const readyDocId = documentReadyFor === (resolvedDocId ?? 'single');

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t('K_DS_EXT_REVIEW_LOADING_SESSION', 'Loading review session...')}</div>
      </div>
    );
  }

  if (error || !session) {
    // Review-ended / gone codes from the reviewer-token endpoint → benign
    // "Review Complete" screen instead of a raw error:
    //   43069 K_REVIEW_NOT_FOUND, 43081 K_REVIEW_TERMINAL_STATUS,
    //   43053 K_NO_ACTIVE_REVIEW,
    //   43096 K_CONTRACT_NOT_FOUND — once the reviewer completes, the snapshot is
    //         parked (REVIEW_COMPLETED) and the token-scoped contract lookup 404s,
    //         so a re-opened mail link / refresh lands here.
    const apiErr = error instanceof WorkpayApiError ? error : null;
    const isReviewEnded = apiErr !== null && [43069, 43081, 43053, 43096].includes(apiErr.code);
    return (
      <div className={styles.container}>
        <div className={isReviewEnded ? styles.done : styles.error}>
          <h2>{isReviewEnded ? t('K_DS_STATUS_REVIEW_COMPLETED', 'Review Complete') : t('K_DS_EXT_REVIEW_ERROR_LOAD_TITLE', 'Unable to load review')}</h2>
          <p>{isReviewEnded ? t('K_DS_EXT_REVIEW_ENDED_BODY', 'This review has been completed. No further changes can be made. Thank you for your participation.') : (error as Error)?.message || t('K_DS_EXT_REVIEW_ERROR_LOAD_BODY', 'This review link may be invalid or expired.')}</p>
        </div>
      </div>
    );
  }

  if (isReviewCompleted) {
    return (
      <div className={styles.container}>
        <div className={styles.done}>
          <h2>{t('K_DS_STATUS_REVIEW_COMPLETED', 'Review Complete')}</h2>
          <p>{session
            ? t('K_DS_EXT_REVIEW_COMPLETED_BODY', 'Thank you, {name}! This review has been completed.').replace('{name}', session.reviewerName)
            : t('K_DS_EXT_REVIEW_COMPLETED_BODY_NO_NAME', 'Thank you! This review has been completed.')}</p>
          <p>{t('K_DS_EXT_REVIEW_COMPLETED_BODY_HINT', 'No further changes can be made.')}</p>
        </div>
      </div>
    );
  }

  const displayTitle = isMultiDoc && activeDocument
    ? activeDocument.documentName
    : session.documentTitle;

  /** The prototype's four steps (`ReviewerDocumentPage.jsx:35-39`), replacing one dense block
   *  of prose above the document that nobody reads on arrival. */
  /**
   * The first two steps describe the SURFACE, and the two surfaces have nothing in common.
   * They used to describe the prototype's own paper — "click the icon in the left margin" —
   * which exists on neither: OnlyOffice comments through its right-click menu, and a PDF
   * cannot be typed into at all, it carries a Highlight / Note toolbar instead. A reviewer who
   * follows an instruction that does not match the screen concludes the screen is broken.
   */
  const surfaceIsPdf = (activeDocument?.fileType ?? session.fileType)?.toLowerCase() === 'pdf';
  const howToSteps = [
    surfaceIsPdf
      ? t(
          'K_DS_REVIEWER_HOW_TO_1_PDF',
          'Mark up the contract with Highlight for wording you mean, or Note to pin a comment anywhere on the page.',
        )
      : t('K_DS_REVIEWER_HOW_TO_1', 'Click into the text and type. Your edit shows in colour and the original wording is kept.'),
    surfaceIsPdf
      ? t(
          'K_DS_REVIEWER_HOW_TO_2_PDF',
          'Both tools work on the latest version only — switch to it if they are greyed out.',
        )
      : t('K_DS_REVIEWER_HOW_TO_2', 'To comment, select the text, then right-click it and choose Add comment.'),
    /* A signing request's reviewer gets ONE send: the server marks them reviewed on submit
       and refuses everything after it. Telling them to "send as often as you need" is how a
       reviewer ends up clicking Mark as reviewed into a refusal. */
    session.targetType === 'ENVELOPE'
      ? t(
          'K_DS_REVIEWER_HOW_TO_3_ENVELOPE',
          'Request changes sends your edits and comments to the client — it also finishes your review, so send once you have everything.',
        )
      : t('K_DS_REVIEWER_HOW_TO_3', 'Request changes sends your edits and comments to the client. Send as often as you need, and reply in the comments.'),
    t('K_DS_REVIEWER_HOW_TO_4', 'Mark as reviewed when you have nothing more to add — your review is not complete until you do.'),
  ];

  return (
    <>
    <ReviewerChrome
      title={displayTitle}
      reviewerName={session.reviewerName}
      contextLine={
        canEdit
          ? t('K_DS_EXT_REVIEW_CONTEXT', 'Workpay owns the wording, so your edits are suggestions.')
          : t('K_DS_EXT_REVIEW_FINISHED_BANNER', 'You have finished your review.')
      }
      howToSteps={howToSteps}
      howToIntro={t('K_DS_REVIEWER_HOW_TO_INTRO', 'Nothing you type changes the contract — it all goes to the client as a suggestion.')}
      /* See ReviewerView. Unlike the portal, a finished reviewer stays on this screen, so
         both of the prototype's two states are reachable here. */
      howToFooter={
        <>
          <b>{t('K_DS_REVIEWER_WHERE_THIS_STANDS', 'Where this review stands')}</b>
          {canEdit
            ? t(
                'K_DS_REVIEWER_NOT_COMPLETE_YET',
                'Your review is not complete until you mark it reviewed.',
              )
            : t('K_DS_EXT_REVIEW_FINISHED_BANNER', 'You have finished your review.')}
        </>
      }
      /* Finished: the prototype's green pill takes the place the two buttons held, so the
         header answers "did I already do this?" instead of just going quiet. */
      reviewedBy={canEdit ? undefined : session.reviewerName}
      actions={
        <>
          <LocaleSwitcher />
          {/* The SAME component the portal's reviewer renders (ds-shared), so the two screens
              carry one set of labels and one geometry rather than each app passing its own
              Button — which is what made the external reviewer's actions 12px grey against the
              portal's 14px brand-outlined, in the same slot of the same chrome.
              Both disappear once this reviewer has finished. Hidden, not disabled: there is
              nothing they can do to make them work again, and a disabled control invites them
              to keep trying. The pill beside them says why. */}
          {canEdit && (
            <ReviewerActions
              mode="edit"
              submitPending={submitMutation.isPending}
              completePending={completeMutation.isPending}
              anyPending={anyActionPending}
              documentReady={readyDocId}
              onSubmit={() => setPendingAction('submit')}
              onComplete={() => setPendingAction('complete')}
              /* Preview-mode only — this screen never offers them. */
              onApprove={() => undefined}
              onRequestChanges={() => undefined}
            />
          )}
        </>
      }
      activityPanel={
                <ReviewActivityPanel
                  targetType={session.targetType}
                  targetId={session.targetId ?? ''}
                  reviews={reviews}
                  mode="reviewer"
                  token={token}
                  /* See ReviewerView: the chrome owns the rail's heading and border. */
                  variant="embedded"
                  loading={historyLoading || activitiesLoading}
                  onNoteSent={handleNoteSent}
                />
      }
    >
      <div className={styles.docRow}>
        {isMultiDoc && (
          <div className={styles.docSidebar}>
            <div className={styles.docSidebarTitle}>{t('K_DOCUMENTS', 'Documents')}</div>
            <div className={styles.docSidebarList}>
              {documents.map(doc => (
                <button
                  key={doc.id}
                  className={`${styles.docSidebarItem} ${doc.id === resolvedDocId ? styles.docSidebarItemActive : ''}`}
                  onClick={() => setActiveDocId(doc.id)}
                >
                  <span className={styles.docSidebarIcon}>&#128196;</span>
                  <span className={styles.docSidebarName}>{doc.documentName}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={styles.editorContainer}>
          <ReviewDocumentSurface
            token={token}
            session={session}
            activeDocument={activeDocument}
            resolvedDocId={resolvedDocId}
            onReady={handleDocumentReady}
          />
        </div>
      </div>
    </ReviewerChrome>

      {/* Copy rows 3 and 4. Both finish this reviewer; only Request changes sends what they
          marked up. */}
      <Modal
        isOpen={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        title={
          pendingAction === 'submit'
            ? t('K_DS_EXT_CONFIRM_REQUEST_CHANGES_TITLE', 'Request changes?')
            : t('K_DS_EXT_CONFIRM_MARK_REVIEWED_TITLE', 'Mark as reviewed?')
        }
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setPendingAction(null);
                setActionError(null);
              }}
            >
              {t('K_BUTTON_CANCEL', 'Cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={submitMutation.isPending || completeMutation.isPending}
              onClick={() =>
                pendingAction === 'submit'
                  ? submitMutation.mutate()
                  : completeMutation.mutate()
              }
            >
              {pendingAction === 'submit'
                ? t('K_DS_EXT_REVIEW_BUTTON_REQUEST_CHANGES', 'Request changes')
                : t('K_DS_EXT_REVIEW_BUTTON_MARK_REVIEWED', 'Mark as reviewed')}
            </Button>
          </>
        }
      >
        <div className={styles.confirmBody}>
          <p>
            {pendingAction === 'submit'
              ? t(
                  'K_DS_EXT_CONFIRM_REQUEST_CHANGES_BODY',
                  'Your edits and comments will be sent to legal. You’ll be told once they have been accepted or declined.',
                )
              : t(
                  'K_DS_EXT_CONFIRM_MARK_REVIEWED_BODY_ANY',
                  'Your review will be marked as complete. Anything you typed or commented on the contract is not sent for review — to send it, use Request changes instead.',
                )}
          </p>
          {!!actionError && <p className={styles.confirmError}>{actionError}</p>}
        </div>
      </Modal>
    </>
  );
}

// ── Document Surface: branches PDF (PdfReviewViewer) vs DOCX (OnlyOffice) ─────

import type { ReviewSession, ReviewDocumentDto, ReviewDocumentVersion } from '@workpay/ds-shared/types/review';

interface ReviewDocumentSurfaceProps {
  token: string;
  session: ReviewSession;
  activeDocument: ReviewDocumentDto | null;
  resolvedDocId: string | null;
  /** The document has settled — rendered, or definitively failed. Must be stable. */
  onReady: () => void;
}

function ReviewDocumentSurface({
  token,
  session,
  activeDocument,
  resolvedDocId,
  onReady,
}: ReviewDocumentSurfaceProps) {
  /**
   * Which renderer this document needs.
   *
   * Read off the DOCUMENT whenever the session carries one, and off the session only when it
   * does not. The split used to be `isMultiDoc ? document : session`, which is a different
   * question: an ENVELOPE review with exactly ONE document is not multi-doc, but it still
   * carries its type and versions per document and leaves the session-level fields null. So a
   * one-document signing request read `session.fileType` = null, decided it was not a PDF, and
   * sent the PDF to OnlyOffice — which answers `43058 K_DOCUMENT_FORMAT_NOT_EDITABLE`
   * (`OnlyOfficeEditorConfigServiceImpl.requireDocx`) and leaves the reviewer at "Failed to
   * load editor" with no way to review at all. Only a TEMPLATE review has no `documents`, and
   * that is the case the session-level fallback is for.
   */
  const activeFileType = (activeDocument?.fileType ?? session.fileType)?.toLowerCase();
  const isPdf = activeFileType === 'pdf';

  const versions: ReviewDocumentVersion[] = useMemo(
    () => activeDocument?.versions ?? session.versions ?? [],
    [activeDocument?.versions, session.versions],
  );
  const resolveFileUrl = useCallback((fileId: string) => reviewerFileUrl(token, fileId), [token]);

  // A PDF NEVER goes to OnlyOffice. OnlyOffice cannot open one, so the editor-config call
  // refuses it (K_TEMPLATE_FORMAT_NOT_EDITABLE) and the reviewer was left looking at "Failed
  // to load editor" with no way to review the document at all. Deliberately not gated on
  // `versions.length` any more: the server seeds v1 for every reviewed document, and if one
  // is somehow still resolving, this viewer shows its own placeholder — which beats handing
  // the document to an editor that is guaranteed to reject it.
  if (isPdf) {
    return (
      <PdfReviewViewer
        key={`pdf-review-${resolvedDocId ?? 'single'}`}
        mode="reviewer"
        token={token}
        reviewDocumentId={resolvedDocId ?? undefined}
        currentReviewerId={session.reviewerId}
        versions={versions}
        resolveFileUrl={resolveFileUrl}
        onReady={onReady}
      />
    );
  }

  // DOCX — co-edited in OnlyOffice. Also the fallback when the type is unknown, which is the
  // safe direction: OnlyOffice renders a DOCX and reports a clear refusal for anything else,
  // whereas the PDF viewer would simply fail to parse it.
  return (
    <ReviewEditorMount
      token={token}
      documentId={resolvedDocId ?? undefined}
      onReady={onReady}
    />
  );
}

// ── OnlyOffice Editor Mount ──────────────────────────────────────────

function ReviewEditorMount({ token, documentId, onReady }: { token: string; documentId?: string; onReady: () => void }) {
  // Held in a ref: this callback is not a reason to tear down and rebuild the editor, which
  // is what adding it to the init effect's deps would do.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { onlyOfficeUrl } = useDsConfig();
  const API_SCRIPT_URL = `${onlyOfficeUrl}/web-apps/apps/api/documents/api.js`;

  useEffect(() => {
    let destroyed = false;

    async function init() {
      try {
        setLoading(true);
        setError(null);

        const config = await reviewService.getReviewEditorConfig(token, documentId);
        if (destroyed) return;

        await loadScript(API_SCRIPT_URL);
        if (destroyed) return;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const DocsAPI = (window as any).DocsAPI;
        if (!DocsAPI) throw new Error(t('K_DS_EXT_REVIEW_EDITOR_ERR_API_NOT_LOADED', 'OnlyOffice API not loaded'));
        if (!containerRef.current) return;

        containerRef.current.innerHTML = '';
        const editorDiv = document.createElement('div');
        editorDiv.id = 'review-editor-' + Date.now();
        editorDiv.style.width = '100%';
        editorDiv.style.height = '100%';
        containerRef.current.appendChild(editorDiv);

        editorRef.current = new DocsAPI.DocEditor(editorDiv.id, {
          ...config,
          height: '100%',
          width: '100%',
          events: {
            onDocumentReady: () => { if (!destroyed) { setLoading(false); onReadyRef.current(); } },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onError: (event: any) => { if (!destroyed) { setError(event.data?.errorDescription || t('K_DS_EXT_REVIEW_EDITOR_ERR_GENERIC', 'Editor error')); onReadyRef.current(); } },
          },
        });
      } catch (err: unknown) {
        if (!destroyed) {
          setError(err instanceof Error ? err.message : t('K_DS_EXT_REVIEW_EDITOR_ERR_LOAD_FAILED', 'Failed to load editor'));
          setLoading(false);
          onReadyRef.current();
        }
      }
    }

    init();

    return () => {
      destroyed = true;
      if (editorRef.current) {
        try { editorRef.current.destroyEditor(); } catch { /* ignore */ }
        editorRef.current = null;
      }
      if (containerRef.current) containerRef.current.innerHTML = '';
    };
  }, [token, documentId, API_SCRIPT_URL, t]);

  return (
    <>
      {loading && <div className={styles.editorLoading}>{t('K_DS_EXT_REVIEW_EDITOR_LOADING', 'Loading document editor...')}</div>}
      {error && <div className={styles.error}><p>{t('K_DS_EXT_REVIEW_EDITOR_ERR_LOAD_FAILED', 'Failed to load editor')}</p><p>{error}</p></div>}
      <div ref={containerRef} style={{ width: '100%', height: '100%', display: loading || error ? 'none' : 'block' }} />
    </>
  );
}

// ── Script loader ────────────────────────────────────────────────────

let scriptLoaded = false;
let scriptLoading = false;
let scriptError: Error | null = null;
const scriptCallbacks: { resolve: () => void; reject: (err: Error) => void }[] = [];

function loadScript(url: string): Promise<void> {
  if (scriptLoaded) return Promise.resolve();
  if (scriptError) return Promise.reject(scriptError);
  return new Promise((resolve, reject) => {
    if (scriptLoading) { scriptCallbacks.push({ resolve, reject }); return; }
    scriptLoading = true;
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => {
      scriptLoaded = true;
      scriptLoading = false;
      resolve();
      scriptCallbacks.forEach((cb) => cb.resolve());
      scriptCallbacks.length = 0;
    };
    script.onerror = () => {
      scriptLoading = false;
      scriptError = new Error(`Failed to load script: ${url}`);
      reject(scriptError);
      scriptCallbacks.forEach((cb) => cb.reject(scriptError!));
      scriptCallbacks.length = 0;
    };
    document.head.appendChild(script);
  });
}
